import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    CircularProgress,
    Button,
    Chip,
    Tooltip
} from '@mui/material';
import { CreditCardOutlined, PersonAddOutlined } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import moment from 'moment';

import RowActionDropdown from './RowActionDropdown';
import { formatINR } from './creditLimitsUtils';

const CreditLimitsTable = ({
    loading,
    accounts,
    totalCount,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    debouncedSearch,
    hasActiveFilters,
    onClearFilters,
    onOpenSetLimit,
    onOpenSettleModal,
    onOpenCreditBalanceModal,
    onOpenDebitBalanceModal,
    onOpenHistoryModal,
    onOpenOnboardModal,
    onOpenStatusModal
}) => {
    const safeAccounts = Array.isArray(accounts) ? accounts : [];

    // Horizontal drag-to-scroll refs & state
    const tableContainerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeftState, setScrollLeftState] = useState(0);

    const handleMouseDown = (e) => {
        const tagName = e.target.tagName;
        if (['INPUT', 'BUTTON', 'A', 'SELECT', 'LABEL', 'SVG', 'PATH'].includes(tagName) || e.target.closest('button')) return;
        setIsDragging(true);
        setStartX(e.pageX - tableContainerRef.current.offsetLeft);
        setScrollLeftState(tableContainerRef.current.scrollLeft);
    };

    const handleMouseUp = () => setIsDragging(false);
    const handleMouseLeave = () => setIsDragging(false);
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - tableContainerRef.current.offsetLeft;
        const walk = (x - startX) * 1.5;
        tableContainerRef.current.scrollLeft = scrollLeftState - walk;
    };

    return (
        <>
            <TableContainer
                ref={tableContainerRef}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
                sx={{
                    overflowX: 'auto',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    userSelect: isDragging ? 'none' : 'auto',
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 },
                    '&::-webkit-scrollbar-track': { bgcolor: 'transparent' }
                }}
            >
                <Table size="small" sx={{ minWidth: 1000 }}>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            {[
                                { id: 'user', label: 'User / Name', minWidth: 200 },
                                { id: 'updated_at', label: 'Updated At', minWidth: 140 },
                                { id: 'type', label: 'Account Type', minWidth: 140 },
                                { id: 'monthly_limit', label: 'Monthly Limit', minWidth: 140 },
                                { id: 'outstanding', label: 'Outstanding', minWidth: 140 },
                                { id: 'balance', label: 'Balance', minWidth: 140 },
                                { id: 'status', label: 'Status', minWidth: 110 },
                                { id: 'actions', label: 'Actions', minWidth: 120, align: 'center' }
                            ].map((col) => (
                                <TableCell
                                    key={col.id}
                                    align={col.align || 'left'}
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: 12,
                                        color: 'text.secondary',
                                        py: 1.5,
                                        minWidth: col.minWidth
                                    }}
                                >
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                    <CircularProgress size={32} />
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Loading credit accounts...
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : safeAccounts.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                    <Box sx={{ maxWidth: 320, mx: 'auto' }}>
                                        <CreditCardOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            No Accounts Found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {debouncedSearch || hasActiveFilters
                                                ? 'Try adjusting your search terms or filters.'
                                                : 'No credit limit records available yet.'}
                                        </Typography>
                                        {debouncedSearch || hasActiveFilters ? (
                                            <Button size="small" variant="outlined" onClick={onClearFilters}>
                                                Clear Filters
                                            </Button>
                                        ) : (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<PersonAddOutlined />}
                                                onClick={onOpenOnboardModal}
                                                sx={{ bgcolor: '#1e40af' }}
                                            >
                                                Onboard First Employee
                                            </Button>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            <AnimatePresence>
                                {safeAccounts.map((row, i) => {
                                    const userType = String(row.user_type || row.role || 'HR Employee');
                                    const isHr = userType.toLowerCase().includes('hr');
                                    const status = String(row.status || 'Active');
                                    const isActive = status.toLowerCase() === 'active';
                                    const limit = Number(row.monthly_limit ?? row.credit_limit ?? 0);
                                    const outstanding = Number(row.current_outstanding ?? row.outstanding ?? row.outstanding_amount ?? 0);
                                    const balance = Number(row.crossbanking_balance ?? row.balance ?? 0);

                                    return (
                                        <motion.tr
                                            key={row.id || row.employee_code || row.email || i}
                                            initial={{ opacity: 0, y: 4 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: i * 0.03, duration: 0.2 }}
                                            style={{ display: 'table-row' }}
                                        >
                                            {/* User / Name */}
                                            <TableCell sx={{ py: 1.5 }}>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={700} color="text.primary">
                                                        {row.employee_name || row.name || row.email?.split('@')[0] || 'N/A'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {row.email || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>

                                            {/* Updated At */}
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                                    {row.updated_at && moment(row.updated_at).isValid()
                                                        ? moment(row.updated_at).format('DD MMM YYYY')
                                                        : 'N/A'}
                                                </Typography>
                                                {row.updated_at && moment(row.updated_at).isValid() && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.disabled"
                                                        sx={{ fontSize: '0.75rem', display: 'block' }}
                                                    >
                                                        {moment(row.updated_at).format('hh:mm A')}
                                                    </Typography>
                                                )}
                                            </TableCell>

                                            {/* Account Type (Pill Badge) */}
                                            <TableCell>
                                                <Chip
                                                    label={userType}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: 11,
                                                        borderRadius: '16px',
                                                        borderColor: isHr ? '#bfdbfe' : '#ddd6fe',
                                                        bgcolor: isHr ? '#eff6ff' : '#f5f3ff',
                                                        color: isHr ? '#1d4ed8' : '#6d28d9'
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Monthly Limit */}
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600} color="text.primary">
                                                    {formatINR(limit)}
                                                </Typography>
                                            </TableCell>

                                            {/* Outstanding */}
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    sx={{
                                                        color: outstanding > 0 ? '#d97706' : 'text.secondary',
                                                        bgcolor: outstanding > 0 ? '#fffbeb' : 'transparent',
                                                        px: outstanding > 0 ? 1 : 0,
                                                        py: 0.25,
                                                        borderRadius: 1,
                                                        display: 'inline-block'
                                                    }}
                                                >
                                                    {formatINR(outstanding)}
                                                </Typography>
                                            </TableCell>

                                            {/* Balance */}
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    sx={{ color: balance < 0 ? '#dc2626' : '#16a34a' }}
                                                >
                                                    {formatINR(balance)}
                                                </Typography>
                                            </TableCell>

                                            {/* Status (Pill Badge) */}
                                            <TableCell>
                                                <Tooltip title={`Click to ${isActive ? 'deactivate' : 'activate'} credit access`} arrow>
                                                    <Chip
                                                        label={isActive ? 'Active' : 'Inactive'}
                                                        size="small"
                                                        onClick={() =>
                                                            onOpenStatusModal && onOpenStatusModal(row, isActive ? 'inactive' : 'active')
                                                        }
                                                        sx={{
                                                            bgcolor: isActive ? '#dcfce7' : '#f1f5f9',
                                                            color: isActive ? '#166534' : '#475569',
                                                            fontWeight: 700,
                                                            fontSize: '0.65rem',
                                                            height: 22,
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                bgcolor: isActive ? '#bbf7d0' : '#e2e8f0',
                                                                transform: 'scale(1.05)'
                                                            }
                                                        }}
                                                    />
                                                </Tooltip>
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell align="center">
                                                <RowActionDropdown
                                                    row={row}
                                                    onOpenSetLimit={onOpenSetLimit}
                                                    onOpenSettleModal={onOpenSettleModal}
                                                    onOpenCreditBalanceModal={onOpenCreditBalanceModal}
                                                    onOpenDebitBalanceModal={onOpenDebitBalanceModal}
                                                    onOpenHistoryModal={onOpenHistoryModal}
                                                    onOpenStatusModal={onOpenStatusModal}
                                                />
                                            </TableCell>
                                        </motion.tr>
                                    );
                                })}
                            </AnimatePresence>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount || safeAccounts.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                sx={{ borderTop: '1px solid', borderColor: 'divider' }}
            />
        </>
    );
};

CreditLimitsTable.propTypes = {
    loading: PropTypes.bool.isRequired,
    accounts: PropTypes.array.isRequired,
    totalCount: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onRowsPerPageChange: PropTypes.func.isRequired,
    debouncedSearch: PropTypes.string,
    hasActiveFilters: PropTypes.bool.isRequired,
    onClearFilters: PropTypes.func.isRequired,
    onOpenSetLimit: PropTypes.func.isRequired,
    onOpenSettleModal: PropTypes.func.isRequired,
    onOpenCreditBalanceModal: PropTypes.func.isRequired,
    onOpenDebitBalanceModal: PropTypes.func.isRequired,
    onOpenHistoryModal: PropTypes.func.isRequired,
    onOpenOnboardModal: PropTypes.func.isRequired,
    onOpenStatusModal: PropTypes.func
};

export default CreditLimitsTable;
